import { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import axios from 'axios';
import { Plus, Edit, Trash2, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getCurrencySymbol } from '../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    price: 0,
    image_path: ''
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [currency, setCurrency] = useState('BRL');

  useEffect(() => {
    fetchData();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setCurrency(response.data.currency || 'BRL');
    } catch (error) {
      console.error('Erro ao carregar configura\u00e7\u00f5es');
    }
  };

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`)
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, image_path: response.data.path });
    } catch (error) {
      alert('Erro ao enviar imagem');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, formData);
      } else {
        await axios.post(`${API}/products`, formData);
      }
      setIsDialogOpen(false);
      setEditingProduct(null);
      setFormData({ name: '', description: '', category_id: '', price: 0, image_path: '' });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao salvar produto');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Deseja realmente excluir este produto?')) {
      try {
        await axios.delete(`${API}/products/${id}`);
        fetchData();
      } catch (error) {
        alert('Erro ao excluir produto');
      }
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      category_id: product.category_id,
      price: product.price,
      image_path: product.image_path || ''
    });
    setIsDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingProduct(null);
    setFormData({ name: '', description: '', category_id: '', price: 0, image_path: '' });
    setIsDialogOpen(true);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || 'Sem categoria';
  };

  const currencySymbol = getCurrencySymbol(currency);

  return (
    <AdminLayout title="Produtos">
      <div className="flex justify-between items-center mb-6">
        <p className="text-white/60 font-inter">Gerencie os produtos do sistema</p>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="create-product-button"
              onClick={openCreateDialog}
              className="bg-[#FF5500] hover:bg-[#CC4400] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Produto
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-oswald text-2xl">
                {editingProduct ? 'Editar Produto' : 'Novo Produto'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-white/80">Nome</Label>
                <Input
                  id="name"
                  data-testid="product-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-[#27272a] border-white/10 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="description" className="text-white/80">Descrição</Label>
                <Textarea
                  id="description"
                  data-testid="product-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-[#27272a] border-white/10 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="category" className="text-white/80">Categoria</Label>
                <Select
                  value={formData.category_id}
                  onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                  required
                >
                  <SelectTrigger data-testid="product-category-select" className="bg-[#27272a] border-white/10 text-white">
                    <SelectValue placeholder="Selecione uma categoria" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#27272a] border-white/10 text-white">
                    {categories.map(category => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="price" className="text-white/80">Preço ({currencySymbol})</Label>
                <Input
                  id="price"
                  data-testid="product-price-input"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                  className="bg-[#27272a] border-white/10 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="image" className="text-white/80">Imagem</Label>
                <div className="flex gap-2">
                  <Input
                    id="image"
                    data-testid="product-image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="bg-[#27272a] border-white/10 text-white"
                    disabled={uploadingImage}
                  />
                </div>
                {formData.image_path && (
                  <img
                    src={`${API}/files/${formData.image_path}?auth=${localStorage.getItem('token')}`}
                    alt="Preview"
                    className="mt-2 h-24 w-24 object-cover rounded-lg border border-white/10"
                  />
                )}
              </div>
              <Button
                data-testid="submit-product-button"
                type="submit"
                className="w-full bg-[#FF5500] hover:bg-[#CC4400] text-white"
                disabled={uploadingImage}
              >
                {editingProduct ? 'Atualizar' : 'Criar'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map(product => (
          <div
            key={product.id}
            data-testid={`product-item-${product.name}`}
            className="bg-[#18181b] border border-white/10 rounded-xl overflow-hidden hover:border-white/20 transition-all"
          >
            {product.image_path ? (
              <img
                src={`${API}/files/${product.image_path}?auth=${localStorage.getItem('token')}`}
                alt={product.name}
                className="h-40 w-full object-cover"
              />
            ) : (
              <div className="h-40 w-full bg-gradient-to-br from-[#27272a] to-[#18181b] flex items-center justify-center">
                <Upload className="w-12 h-12 text-white/20" />
              </div>
            )}
            <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="text-lg font-oswald font-bold text-white">{product.name}</h3>
                  <p className="text-sm text-white/60 font-inter">{getCategoryName(product.category_id)}</p>
                </div>
                <span className="font-oswald font-bold text-[#FF5500]">{currencySymbol} {product.price.toFixed(2)}</span>
              </div>
              <p className="text-white/60 text-sm font-inter mb-4 line-clamp-2">{product.description}</p>
              <div className="flex gap-2">
                <button
                  data-testid={`edit-product-${product.name}`}
                  onClick={() => openEditDialog(product)}
                  className="flex-1 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-inter transition-all flex items-center justify-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Editar
                </button>
                <button
                  data-testid={`delete-product-${product.name}`}
                  onClick={() => handleDelete(product.id)}
                  className="flex-1 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm font-inter transition-all flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Excluir
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {products.length === 0 && (
        <div className="text-center py-12 text-white/40 font-inter">
          Nenhum produto cadastrado. Clique em "Novo Produto" para começar.
        </div>
      )}
    </AdminLayout>
  );
};

export default Products;